import { createFileRoute, Outlet } from '@tanstack/react-router';

import { useSyncEaiControlledDeviceIds } from '@/hooks/use-sync-eai-controlled-device-ids';
import { jotaiStore } from '@/providers/jotai/jotai-store';
import { getDeviceControlPointIdMapQuery } from '@/queries/gaia-api/get-device-control-point-id-map-query';
import { getSequencerDevicesQuery } from '@/queries/gaia-api/get-sequencer-devices-query';
import { listEaiControlledDeviceIdsQuery } from '@/queries/gaia-api/scheduled-app';
import CurrentFacilityAtoms from '@/state/current-facility.state';
import UserStateAtoms from '@/state/user.state';

const AgentRouteOutlet = () => {
  useSyncEaiControlledDeviceIds();
  return <Outlet />;
};

export const Route = createFileRoute('/$org/$agent')({
  loader: ({ params, context }) => {
    const userState = jotaiStore.get(UserStateAtoms.user);
    const org = userState?.organizations?.byName[params.org];
    const agentId = userState?.facilities?.byShortName[params.agent].agents[0].agentId;
    const orgDisplayName = org?.displayName;

    // Update facility cache before render to prevent flash of wrong facility
    if (context?.updateFacilityCache) {
      context.updateFacilityCache({
        org: params.org,
        agent: params.agent,
        orgDisplayName,
      });
    }
    // Prefetch BFF enhanced-device queries
    if (context?.queryClient && context?.gaiaClient && agentId) {
      context.queryClient.prefetchQuery(
        getDeviceControlPointIdMapQuery({
          agentId,
          client: context.gaiaClient,
        })
      );
      context.queryClient.prefetchQuery(
        getSequencerDevicesQuery({
          agentId,
          client: context.gaiaClient,
        })
      );
      context.queryClient.prefetchQuery(
        listEaiControlledDeviceIdsQuery({
          agentId,
          client: context.gaiaClient,
        })
      );
    }
  },
  onLeave: ({ context }) => {
    const orgId = jotaiStore.get(CurrentFacilityAtoms.organizationId);
    const agentId = jotaiStore.get(CurrentFacilityAtoms.agentId);
    if (context?.queryClient && orgId && agentId) {
      // Invalidate all atlast queries for the agent
      context.queryClient.invalidateQueries({ queryKey: [orgId, agentId] });
      // Invalidate all gaia queries for the agent
      context.queryClient.invalidateQueries({ queryKey: [agentId] });
    }
  },
  component: AgentRouteOutlet,
});
